import { useEffect, useState } from "react";
import { Building2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { SearchInput, useListParams } from "@/components/shared/ListChrome";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const CONNECTION = { PLACEMENT: "Placement", OFFER: "Offered to you" };
const Connection = ({ list }) => <span className="flex flex-wrap gap-1">{list.map((c) => <span key={c} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${c === "PLACEMENT" ? "border-jigsaw-success/30 bg-jigsaw-successBg text-jigsaw-success" : "border-jigsaw-border bg-white text-jigsaw-muted"}`}>{CONNECTION[c]}</span>)}</span>;

/* D7: only properties connected to this organisation's placements or released offers; address and type only. */
export default function ClientProperties() {
  const { q, page, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/client/properties", { params: { q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true)); }, [q, page]);
  if (error) return <ErrorState testId="client-properties-error" />;
  return (
    <div data-testid="client-properties-page" className="space-y-5">
      <PageHeader title="Properties" subtitle="Properties where your organisation has had a placement, or that Jigsaw has offered to you. Supplier contacts and commercial details are held by Jigsaw." />
      <FilterBar testId="client-properties-filters" summary={data ? `${data.total} propert${data.total === 1 ? "y" : "ies"}${data.counts ? ` · ${data.counts.PLACEMENT} with placements · ${data.counts.OFFER} offered` : ""}` : null}>
        <SearchInput testId="client-properties-search" value={q} onChange={setQ} placeholder="Search name, town or postcode" />
      </FilterBar>
      {!data && <LoadingState testId="client-properties-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="client-properties-empty" icon={Building2} title="No properties yet" description="Properties appear here once Jigsaw offers you accommodation or confirms a placement." />}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="client-properties-stacked" items={data.items} title={(p) => p.name} status={(p) => <Connection list={p.connection} />}
            fields={[{ label: "Address", value: (p) => [p.address_line1, p.city, p.postcode].filter(Boolean).join(", ") || "—", full: true }, { label: "Type", value: (p) => p.property_type || "—" }, { label: "Stays", value: (p) => p.stays }]} />
          <div className="hidden sm:block">
            <Table data-testid="client-properties-table">
              <TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Address</TableHead><TableHead>Type</TableHead><TableHead>Connection</TableHead><TableHead className="text-right">Stays</TableHead><TableHead>Latest check-in</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow key={p.id} data-testid={`client-property-row-${p.id}`}>
                    <TableCell className="font-medium text-jigsaw-navy">{p.name}</TableCell>
                    <TableCell className="text-[13px]"><span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-jigsaw-muted" />{[p.address_line1, p.city, p.postcode].filter(Boolean).join(", ") || "—"}</span></TableCell>
                    <TableCell>{p.property_type || "—"}</TableCell>
                    <TableCell><Connection list={p.connection} /></TableCell>
                    <TableCell className="text-right font-mono-ref">{p.stays}</TableCell>
                    <TableCell className="font-mono-ref text-[13px]">{p.latest_check_in || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination testId="client-properties-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
